import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Loader2, DollarSign, MapPin, Clock, TrendingUp, Tag, X, ArrowRightLeft,
  FileText, Building2, Send, CheckCircle, Bookmark, BookmarkCheck
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { LuxuryBackground, Card, GoldButton, PageHero, inputClassLight as inputClass, labelClassLight as labelClass } from "../components/shared/UIComponents";
import { usePageNavigate } from "../hooks/usePageNavigate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const loanLabels = { fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR", cash_out: "Cash-Out Refi", construction: "Construction", ground_up: "Ground Up", multifamily: "Multifamily", commercial: "Commercial" };

export default function LoanMarket() {
  const { user } = useAuth();
  const { navigateToEntity } = usePageNavigate();
  const [listings, setListings] = useState([]);
  const [myListings, setMyListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("browse");
  const [listModal, setListModal] = useState(false);
  const [offerModal, setOfferModal] = useState(null);
  const [watchedIds, setWatchedIds] = useState(new Set());
  const token = localStorage.getItem("ic_token");
  const wlHeaders = { Authorization: `Bearer ${token}` };

  const fetchListings = () => {
    Promise.all([
      axios.get(`${API}/market/listings`).then(r => setListings(r.data.listings || [])).catch(() => {}),
      axios.get(`${API}/market/my-listings`).then(r => setMyListings(r.data.listings || [])).catch(() => {}),
      token ? axios.get(`${API}/watchlist`, { headers: wlHeaders }).then(r => {
        setWatchedIds(new Set(r.data.items.filter(w => w.entity_type === "deal").map(w => w.entity_id)));
      }).catch(() => {}) : Promise.resolve(),
    ]).finally(() => setLoading(false));
  };
  useEffect(fetchListings, []);

  const toggleWatch = async (leadId) => {
    if (!token) return;
    const isWatched = watchedIds.has(leadId);
    try {
      if (isWatched) {
        await axios.delete(`${API}/watchlist/deal/${leadId}`, { headers: wlHeaders });
        setWatchedIds(prev => { const n = new Set(prev); n.delete(leadId); return n; });
      } else {
        await axios.post(`${API}/watchlist?entity_type=deal&entity_id=${leadId}`, {}, { headers: wlHeaders });
        setWatchedIds(prev => new Set([...prev, leadId]));
      }
    } catch {}
  };

  const isLender = user?.role === "lender" || user?.role === "investor" || user?.role === "admin";

  return (
    <div className="relative" data-testid="loan-market-page">
      <PageHero eyebrow="Secondary Market" title="Loan Trading Desk"
        description="Buy and sell performing loans on the Imaani Capital secondary market." />
      <div className="relative">
        <LuxuryBackground />
        <section className="relative z-10 mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
          {/* Tabs */}
          <div className="flex gap-2 mb-6 flex-wrap items-center">
            {["browse", ...(isLender ? ["my-listings"] : [])].map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)} data-testid={`market-tab-${tab}`}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  activeTab === tab ? "bg-gold text-[#022c22]" : "bg-cream text-[#444] hover:text-[#0A0A0A] border border-cream-warm"
                }`}>{tab === "my-listings" ? "My Listings" : "Browse Market"}</button>
            ))}
            {isLender && (
              <GoldButton onClick={() => setListModal(true)} variant="secondary" className="!px-4 !py-2.5 !text-xs ml-auto" data-testid="list-loan-btn">
                + List a Loan
              </GoldButton>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[
              { label: "Active Listings", value: listings.length, icon: FileText, color: "text-blue-600" },
              { label: "Total Volume", value: `$${(listings.reduce((s, l) => s + l.asking_price, 0) / 1e6).toFixed(1)}M`, icon: DollarSign, color: "text-gold-dark" },
              { label: "My Listings", value: myListings.length, icon: Tag, color: "text-emerald-600" },
              { label: "Avg Yield", value: listings.length > 0 ? `${(listings.reduce((s, l) => s + l.current_rate, 0) / listings.length).toFixed(1)}%` : "—", icon: TrendingUp, color: "text-amber-600" },
            ].map((s, i) => (
              <motion.div key={s.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
                <Card hover={false}>
                  <div className="flex items-center gap-3">
                    <div className="rounded-xl bg-gold/10 p-2.5 border border-cream-warm"><s.icon className={`h-5 w-5 ${s.color}`} /></div>
                    <div>
                      <div className="text-2xl font-semibold text-[#0A0A0A]">{s.value}</div>
                      <div className="text-xs text-[#888]">{s.label}</div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>
          ) : activeTab === "browse" ? (
            listings.length === 0 ? (
              <Card hover={false}>
                <div className="text-center py-12 text-[#999]">
                  <ArrowRightLeft className="h-12 w-12 mx-auto mb-3 text-cream/20" />
                  <p className="text-lg mb-2">No loans listed yet</p>
                  <p className="text-sm">Be the first to list a performing loan on the secondary market.</p>
                </div>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {listings.map((l, i) => (
                  <motion.div key={l.listing_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                    <ListingCard listing={l} isOwner={l.seller_id === user?.user_id} onOffer={() => setOfferModal(l)}
                      watched={watchedIds.has(l.lead_id)} onToggleWatch={() => toggleWatch(l.lead_id)}
                      onViewDeal={(leadId) => navigateToEntity("deal", leadId)} />
                  </motion.div>
                ))}
              </div>
            )
          ) : (
            myListings.length === 0 ? (
              <Card hover={false}>
                <div className="text-center py-12 text-[#999]">
                  <Tag className="h-12 w-12 mx-auto mb-3 text-cream/20" />
                  <p>You haven't listed any loans yet.</p>
                </div>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {myListings.map((l, i) => (
                  <motion.div key={l.listing_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                    <MyListingCard listing={l} />
                  </motion.div>
                ))}
              </div>
            )
          )}
        </section>
      </div>

      {/* List Loan Modal */}
      <AnimatePresence>
        {listModal && <ListLoanModal onClose={() => setListModal(false)} onSuccess={fetchListings} />}
        {offerModal && <OfferModal listing={offerModal} onClose={() => setOfferModal(null)} onSuccess={fetchListings} />}
      </AnimatePresence>
    </div>
  );
}

function ListingCard({ listing, isOwner, onOffer, watched, onToggleWatch, onViewDeal }) {
  const { property_address, loan_type, state, asking_price, current_rate, current_balance, remaining_term_months, offers } = listing;
  const photo = listing.photos?.[0] || listing.photo_url;
  const riskScore = listing.risk_score || listing.deal_snapshot?.risk_score;
  const loanAmount = listing.loan_amount || listing.deal_snapshot?.loan_amount || current_balance;
  const dealNumber = listing.deal_number || listing.deal_snapshot?.deal_number;

  return (
    <Card hover={true} data-testid={`listing-${listing.listing_id}`}>
      {/* Property Photo Hero */}
      <div className="relative -mx-5 -mt-5 mb-4 rounded-t-2xl overflow-hidden bg-gradient-to-br from-emerald-900 to-emerald-950 h-36 cursor-pointer"
        onClick={() => onViewDeal && listing.lead_id && onViewDeal(listing.lead_id)}>
        {photo ? (
          <img src={photo} alt={property_address} className="w-full h-full object-cover opacity-80" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Building2 className="h-12 w-12 text-emerald-700/30" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute bottom-3 left-4 right-4">
          <div className="text-white text-sm font-bold">{property_address || "Property"}</div>
          <div className="flex items-center gap-2 mt-1">
            <span className="px-2 py-0.5 rounded-md text-[9px] font-bold bg-white/20 text-white backdrop-blur-sm">{loanLabels[loan_type] || loan_type}</span>
            {state && <span className="text-[10px] text-white/70 flex items-center gap-0.5"><MapPin className="h-2.5 w-2.5" />{state}</span>}
            {dealNumber && <span className="text-[10px] text-white/50 ml-auto">{dealNumber}</span>}
          </div>
        </div>
        {/* Risk Score Badge */}
        {riskScore != null && (
          <div className="absolute top-3 right-3">
            <div className={`px-2 py-1 rounded-lg text-[10px] font-bold backdrop-blur-sm border ${
              riskScore >= 70 ? "bg-emerald-500/20 text-emerald-300 border-emerald-400/30" :
              riskScore >= 40 ? "bg-amber-500/20 text-amber-300 border-amber-400/30" :
              "bg-red-500/20 text-red-300 border-red-400/30"
            }`}>
              Risk: {riskScore}
            </div>
          </div>
        )}
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-3 gap-3 mb-3">
        <div className="rounded-lg bg-cream border border-cream-warm p-2 text-center">
          <div className="text-sm font-semibold text-[#0A0A0A]">${(loanAmount || 0).toLocaleString()}</div>
          <div className="text-[9px] text-[#999]">Loan Amount</div>
        </div>
        <div className="rounded-lg bg-cream border border-cream-warm p-2 text-center">
          <div className="text-sm font-semibold text-[#0A0A0A]">{current_rate}%</div>
          <div className="text-[9px] text-[#999]">Rate</div>
        </div>
        <div className="rounded-lg bg-cream border border-cream-warm p-2 text-center">
          <div className="text-sm font-semibold text-[#0A0A0A]">{remaining_term_months}mo</div>
          <div className="text-[9px] text-[#999]">Term Left</div>
        </div>
      </div>

      {listing.description && <p className="text-xs text-[#888] mb-3 line-clamp-2">{listing.description}</p>}

      {/* Actions — no seller/borrower info */}
      <div className="flex items-center justify-between pt-3 border-t border-cream-warm">
        <div className="text-xs font-semibold text-gold-dark">${asking_price?.toLocaleString()} <span className="text-[10px] text-[#999] font-normal">asking</span></div>
        <div className="flex items-center gap-2">
          {onToggleWatch && (
            <button onClick={onToggleWatch} data-testid={`watch-loan-${listing.listing_id}`}
              className={`p-1.5 rounded-lg transition-colors ${watched ? "text-amber-500 bg-amber-50" : "text-[#ccc] hover:text-amber-400"}`}>
              {watched ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
            </button>
          )}
          {(offers?.length || 0) > 0 && <span className="text-[10px] text-amber-600">{offers.length} offer{offers.length > 1 ? "s" : ""}</span>}
          {listing.lead_id && onViewDeal && (
            <button onClick={() => onViewDeal(listing.lead_id)} data-testid={`view-deal-${listing.listing_id}`}
              className="px-3 py-1.5 rounded-lg bg-emerald-700 text-white text-xs font-medium hover:bg-emerald-800 transition-colors">
              View Deal
            </button>
          )}
          {!isOwner && (
            <button onClick={onOffer} data-testid={`make-offer-${listing.listing_id}`}
              className="px-3 py-1.5 rounded-lg bg-gold/15 text-gold border border-gold/25 text-xs font-medium hover:bg-gold/25 transition-colors">
              Make Offer
            </button>
          )}
        </div>
      </div>
    </Card>
  );
}

function MyListingCard({ listing }) {
  const { property_address, asking_price, status, offers, sold_price } = listing;
  return (
    <Card hover={false} data-testid={`my-listing-${listing.listing_id}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-sm font-medium text-[#0A0A0A]">{property_address || "Property"}</div>
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold mt-1 inline-block ${
            status === "sold" ? "bg-emerald-100 text-emerald-600" : "bg-gold/15 text-gold-dark"
          }`}>{status === "sold" ? "SOLD" : "ACTIVE"}</span>
        </div>
        <div className="text-right">
          <div className="text-lg font-semibold text-gold-dark">${asking_price?.toLocaleString()}</div>
          {sold_price && <div className="text-xs text-emerald-600">Sold: ${sold_price.toLocaleString()}</div>}
        </div>
      </div>
      {(offers?.length || 0) > 0 && (
        <div className="space-y-2">
          <div className="text-xs text-[#888]">{offers.length} offer{offers.length > 1 ? "s" : ""}</div>
          {offers.map((o, i) => (
            <div key={o.offer_id || i} className="flex items-center justify-between p-2 rounded-lg border border-cream-warm bg-[#022c22]/40">
              <div>
                <span className="text-sm text-[#0A0A0A]">{o.buyer_name}</span>
                <span className="text-xs text-[#999] ml-2">${o.offer_price?.toLocaleString()}</span>
              </div>
              <span className={`text-[10px] font-medium ${o.status === "accepted" ? "text-emerald-600" : "text-amber-600"}`}>{o.status}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function ListLoanModal({ onClose, onSuccess }) {
  const [form, setForm] = useState({ lead_id: "", asking_price: "", remaining_term_months: "", current_rate: "", current_balance: "", description: "", performance_notes: "" });
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    axios.get(`${API}/portal/lender/my-term-sheets`).then(r => setDeals(r.data.term_sheets || [])).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const payload = {
        lead_id: form.lead_id,
        asking_price: Number(form.asking_price),
        remaining_term_months: Number(form.remaining_term_months),
        current_rate: Number(form.current_rate),
        current_balance: Number(form.current_balance),
        description: form.description || null,
        performance_notes: form.performance_notes || null,
      };
      await axios.post(`${API}/market/listings`, payload);
      onSuccess();
      onClose();
    } catch (err) { setError(err.response?.data?.detail || "Failed to list loan"); }
    finally { setLoading(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95 }}
        className="w-full max-w-lg rounded-2xl border border-cream-warm bg-[#03352a]/98 backdrop-blur-xl p-8" onClick={e => e.stopPropagation()}
        data-testid="list-loan-modal">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-[#0A0A0A]">List Loan for Sale</h3>
          <button onClick={onClose} className="text-[#999] hover:text-[#0A0A0A]"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={labelClass}>Select Deal</label>
            <select required value={form.lead_id} onChange={e => set("lead_id", e.target.value)} className={inputClass} data-testid="list-deal-select">
              <option value="">Choose a deal...</option>
              {deals.map(d => <option key={d.ts_id} value={d.lead_id}>${d.loan_amount?.toLocaleString()} — {d.lead_id?.slice(0, 8)}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelClass}>Asking Price ($)</label>
              <input required type="number" value={form.asking_price} onChange={e => set("asking_price", e.target.value)} className={inputClass} placeholder="500000" data-testid="list-asking-price" /></div>
            <div><label className={labelClass}>Current Balance ($)</label>
              <input required type="number" value={form.current_balance} onChange={e => set("current_balance", e.target.value)} className={inputClass} placeholder="480000" data-testid="list-balance" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelClass}>Current Rate (%)</label>
              <input required type="number" step="0.1" value={form.current_rate} onChange={e => set("current_rate", e.target.value)} className={inputClass} placeholder="11.5" data-testid="list-rate" /></div>
            <div><label className={labelClass}>Remaining Term (months)</label>
              <input required type="number" value={form.remaining_term_months} onChange={e => set("remaining_term_months", e.target.value)} className={inputClass} placeholder="10" data-testid="list-term" /></div>
          </div>
          <div><label className={labelClass}>Description</label>
            <textarea value={form.description} onChange={e => set("description", e.target.value)} className={`${inputClass} min-h-[80px] resize-none`} placeholder="Describe the loan performance..." data-testid="list-description" /></div>
          {error && <div className="text-sm text-red-600 border border-red-400/30 rounded-xl p-3 bg-red-500/10" data-testid="list-error">{error}</div>}
          <GoldButton type="submit" disabled={loading} className="w-full !py-3" data-testid="list-submit-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : "List on Market"}
          </GoldButton>
        </form>
      </motion.div>
    </motion.div>
  );
}

function OfferModal({ listing, onClose, onSuccess }) {
  const [price, setPrice] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      await axios.post(`${API}/market/listings/${listing.listing_id}/offer`, { offer_price: Number(price), message: message || null });
      onSuccess();
      onClose();
    } catch (err) { setError(err.response?.data?.detail || "Failed to submit offer"); }
    finally { setLoading(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95 }}
        className="w-full max-w-md rounded-2xl border border-cream-warm bg-[#03352a]/98 backdrop-blur-xl p-8" onClick={e => e.stopPropagation()}
        data-testid="offer-modal">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-[#0A0A0A]">Make an Offer</h3>
          <button onClick={onClose} className="text-[#999] hover:text-[#0A0A0A]"><X className="h-5 w-5" /></button>
        </div>
        <div className="mb-4 p-3 rounded-xl border border-cream-warm bg-cream">
          <div className="text-sm text-[#0A0A0A]">{listing.property_address}</div>
          <div className="text-xs text-[#999] mt-1">Asking: <span className="text-gold font-medium">${listing.asking_price?.toLocaleString()}</span></div>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><label className={labelClass}>Your Offer Price ($)</label>
            <input required type="number" value={price} onChange={e => setPrice(e.target.value)} className={inputClass} placeholder={String(listing.asking_price)} data-testid="offer-price" /></div>
          <div><label className={labelClass}>Message (optional)</label>
            <textarea value={message} onChange={e => setMessage(e.target.value)} className={`${inputClass} min-h-[60px] resize-none`} placeholder="Any notes for the seller..." data-testid="offer-message" /></div>
          {error && <div className="text-sm text-red-600 border border-red-400/30 rounded-xl p-3 bg-red-500/10">{error}</div>}
          <GoldButton type="submit" disabled={loading} className="w-full !py-3" data-testid="offer-submit-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : <><Send className="h-4 w-4 mr-2 inline" />Submit Offer</>}
          </GoldButton>
        </form>
      </motion.div>
    </motion.div>
  );
}
